/**
 * iter318 wave 9 — Locked Accounts admin panel.
 *
 * Renders inline above the Users tab list ONLY when there are
 * currently-locked accounts. Support staff can review each row + one-
 * click unlock the account (writes to POST /api/auth/admin/users/{id}/unlock).
 * Auto-refreshes after every unlock action.
 *
 * Data-source: GET /api/auth/admin/users/locked (super_admin: global,
 * tenant admin: tenant-scoped — the backend enforces the scope).
 */
import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Lock, Unlock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../services/api';

const _fmtRelative = (iso) => {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const mins = Math.max(0, Math.round((then - now) / 60000));
    if (mins <= 0) return 'expiring';
    if (mins < 60) return `${mins} min`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ${mins % 60}m`;
  } catch {
    return '';
  }
};

export default function LockedAccountsPanel({ onUnlocked }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);   // start true so the initial mount doesn't flash an empty card
  const [hasFetched, setHasFetched] = useState(false);
  const [unlockingId, setUnlockingId] = useState(null);

  const fetchLocked = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/auth/admin/users/locked');
      setRows(res.data?.locked || []);
    } catch (e) {
      // Non-admins get 403 — silently render nothing.
      if (e?.response?.status !== 403) {
        console.error('[LockedAccountsPanel] load failed:', e?.message);
      }
      setRows([]);
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  }, []);

  useEffect(() => {
    fetchLocked();
  }, [fetchLocked]);

  const handleUnlock = async (user) => {
    setUnlockingId(user.id);
    try {
      await api.post(`/auth/admin/users/${user.id}/unlock`);
      toast.success(`Unlocked ${user.email}`);
      await fetchLocked();
      if (onUnlocked) onUnlocked(user);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Failed to unlock ${user.email}`
      );
    } finally {
      setUnlockingId(null);
    }
  };

  // iter281 fix — Hide the whole panel on initial-mount loading too,
  // so admins don't see an empty amber card flash before the fetch
  // returns. Panel appears only when we know there IS ≥1 locked user.
  if (!hasFetched || rows.length === 0) return null;

  return (
    <Card className="mb-4 border-amber-300 bg-amber-50/50" data-testid="locked-accounts-panel">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-amber-900 text-base">
          <Lock className="h-4 w-4" />
          Locked Accounts
          <Badge variant="outline" className="bg-white text-amber-800 border-amber-300 ml-1">
            {rows.length}
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchLocked}
            className="ml-auto h-7 px-2"
            data-testid="locked-accounts-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-xs text-amber-800 mb-3">
          These accounts hit the failed-login threshold and are temporarily locked.
          Unlock to restore sign-in access immediately.
        </p>
        <div className="space-y-1.5">
          {rows.map((u) => (
            <div
              key={u.id}
              className="flex items-center justify-between rounded-md border border-amber-200 bg-white px-3 py-2 text-sm"
              data-testid={`locked-row-${u.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">
                  {u.name || u.email}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {u.email}
                  <span className="mx-1.5 text-slate-300">·</span>
                  <span title="Failed attempts in the current window">
                    {u.failed_attempts || 0} bad attempts
                  </span>
                  {u.locked_until && (
                    <>
                      <span className="mx-1.5 text-slate-300">·</span>
                      <span title={u.locked_until}>
                        auto-unlocks in {_fmtRelative(u.locked_until)}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="ml-3 h-8 border-amber-400 text-amber-900 hover:bg-amber-100"
                onClick={() => handleUnlock(u)}
                disabled={unlockingId === u.id}
                data-testid={`unlock-btn-${u.id}`}
              >
                <Unlock className="h-3.5 w-3.5 mr-1" />
                {unlockingId === u.id ? 'Unlocking…' : 'Unlock'}
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
